import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { MyRequestRow } from '@his/shared';
import { publicGet } from '@/lib/public-api';
import { dateTime } from '@/lib/utils';
import { EmptyState } from '@/components/ui/layout';
import { Button, SkeletonText, Spinner, StatusBadge } from '@/components/ui/primitives';
import { usePublicAuth } from './PublicLayout';
import { IdentityChangeAlerts } from './IdentityChangeAlerts';

const TITLES: Record<string, string> = {
  opd: 'Doctor appointment',
  lab: 'Lab tests',
  order: 'Medicine order',
};

/**
 * Everything this person has asked for, at every facility.
 *
 * No single hospital can produce this view, and none of them can see it — it
 * is assembled per patient on the platform side, which is exactly why the
 * directory account does not belong to any tenant.
 */
/**
 * The detail line carries whatever fits the row — a requested time, a basket
 * summary, a rejection reason. A time arrives as an ISO instant, which nobody
 * wants to read, so it is turned back into a date here rather than being
 * formatted server-side where the reader's locale is unknown.
 */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/;
const describeDetail = (detail: string): string =>
  ISO_INSTANT.test(detail) ? `Asked for ${dateTime(detail)}` : detail;

export function MyActivityPage() {
  const { account, loading, requireSignIn } = usePublicAuth();

  const activity = useQuery({
    queryKey: ['public-activity'],
    queryFn: () => publicGet<MyRequestRow[]>('/me/activity'),
    enabled: Boolean(account),
  });

  if (loading) return <SkeletonText lines={3} />;

  if (!account) {
    return (
      <EmptyState
        title="Sign in to see your requests"
        description="Your appointments and orders are tied to your phone number."
        action={<Button onClick={requireSignIn}>Sign in</Button>}
      />
    );
  }

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">My requests</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Signed in as {account.name ?? account.phone}
        </p>
      </div>

      <IdentityChangeAlerts />

      <PeopleUnderMyNumber />
      <h2 className="mb-2 mt-6 text-lg font-semibold">My requests</h2>
      {activity.isLoading ? (
        <Spinner />
      ) : activity.data && activity.data.length > 0 ? (
        <div className="space-y-2">
          {activity.data.map((row) => (
            <div key={row.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{row.title || TITLES[row.kind]}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {row.orgName} · {row.branchName}
                    {row.city ? `, ${row.city}` : ''}
                  </p>
                </div>
                <StatusBadge status={row.status} />
              </div>

              {row.detail && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {row.status === 'rejected' ? `Reason: ${row.detail}` : describeDetail(row.detail)}
                </p>
              )}

              <p className="mt-2 text-xs text-muted-foreground">Sent {dateTime(row.createdAt)}</p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title="Nothing yet"
          description="Requests and orders you send from this site will show up here."
        />
      )}
    </>
  );
}

interface PersonRow {
  personId?: string;
  id?: string;
  uhid: string | null;
  fullName: string;
  sex: string | null;
  dob: string | null;
}

function PeopleUnderMyNumber() {
  const navigate = useNavigate();
  const people = useQuery({
    queryKey: ['public-people'],
    queryFn: () => publicGet<PersonRow[]>('/me/people'),
  });
  return (
    <section className="mb-6 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-medium">People under your number</h2>
          <p className="text-xs text-muted-foreground">Children and dependants you book for. One number covers the whole family.</p>
        </div>
        <Button size="sm" onClick={() => navigate('/find/me/people/new')}>Add a person</Button>
      </div>
      <div className="mt-3 space-y-1">
        {people.isLoading ? (
          <Spinner />
        ) : people.data && people.data.length > 0 ? (
          people.data.map((person) => (
            <div key={person.personId ?? person.id ?? person.fullName} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span>{person.fullName}</span>
              <span className="text-xs text-muted-foreground">
                {[person.sex && person.sex !== 'unknown' ? person.sex : null, person.dob ? String(person.dob).slice(0, 10) : null, person.uhid].filter(Boolean).join(' · ')}
              </span>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">Only you so far.</p>
        )}
      </div>
    </section>
  );
}
